// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>

import type {
  Meta, Recall, RecallDetail, RecallsPayload, StatsPayload, TaxonomyTerm,
  HazardClass, VehiclePayload,
} from './types';

/**
 * Every data URL except meta.json is versioned by the data's as-at date.
 *
 * The JS bundle is content-hashed but the JSON files are not, and GitHub Pages
 * serves them with a ten-minute cache. Without this, a returning visitor inside
 * that window runs the NEW bundle against the OLD numbers — which is worse than
 * either being stale, because the figures on the page then disagree with each
 * other. meta.json is always revalidated so the version is never itself stale.
 */
let version = '';

async function getJson<T>(url: string, opts?: RequestInit): Promise<T> {
  const res = await fetch(url, opts);
  if (!res.ok) throw new Error(`${url} → HTTP ${res.status}`);
  return res.json() as Promise<T>;
}

const v = (p: string) => `${p}${version ? `?v=${version}` : ''}`;

export interface Bundle {
  meta: Meta;
  hazards: HazardClass[];
  hazardById: Map<number, HazardClass>;
  terms: TaxonomyTerm[];
  termById: Map<number, TaxonomyTerm>;
  recalls: Recall[];
  stats: StatsPayload;
}

let bundle: Bundle | null = null;

export async function loadBundle(signal?: AbortSignal): Promise<Bundle> {
  if (bundle) return bundle;
  const meta = await getJson<Meta>('data/meta.json', { cache: 'no-cache', signal });
  // Keyed on the pipeline's content hash, so a same-day rebuild busts the cache.
  version = meta.dataVersion || meta.asAt;

  const [payload, stats] = await Promise.all([
    getJson<RecallsPayload>(v('data/recalls.json'), { signal }),
    getJson<StatsPayload>(v('data/stats.json'), { signal }),
  ]);

  const recalls: Recall[] = payload.rows.map(
    ([key, title, supplier, date, cats, hazard, onSaleDays, channel, haystack, source]) => ({
      key, title, supplier, date, cats, hazard, onSaleDays, channel, haystack,
      source: source ?? 'accc',
      year: date ? Number(date.slice(0, 4)) : 0,
    }),
  );

  bundle = {
    meta,
    hazards: payload.hazards,
    hazardById: new Map(payload.hazards.map((h) => [h.id, h])),
    terms: payload.terms,
    termById: new Map(payload.terms.map((t) => [t.id, t])),
    recalls,
    stats,
  };
  return bundle;
}

export function getBundle(): Bundle {
  if (!bundle) throw new Error('bundle not loaded');
  return bundle;
}

/* ── lazily-loaded detail buckets ── */

let detailIndex: { bucketSize: number; order: string[] } | null = null;
const bucketCache = new Map<number, Record<string, RecallDetail>>();
let positionOf: Map<string, number> | null = null;

export async function loadDetail(key: string): Promise<RecallDetail | null> {
  if (!detailIndex) {
    detailIndex = await getJson<{ bucketSize: number; order: string[] }>(v('data/detail/index.json'));
    positionOf = new Map(detailIndex.order.map((k, i) => [k, i]));
  }
  const pos = positionOf!.get(key);
  if (pos == null) return null;
  const bucket = Math.floor(pos / detailIndex.bucketSize);
  if (!bucketCache.has(bucket)) {
    bucketCache.set(bucket, await getJson<Record<string, RecallDetail>>(v(`data/detail/${bucket}.json`)));
  }
  return bucketCache.get(bucket)![key] ?? null;
}

let vehicles: VehiclePayload | null = null;
export async function loadVehicles(): Promise<VehiclePayload> {
  if (!vehicles) vehicles = await getJson<VehiclePayload>(v('data/vehicles.json'));
  return vehicles;
}
